import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * TeacherLoginPage
 * - Step 1: enter email + call POST /api/send-email-code
 * - Step 2: enter 6-digit code + call POST /api/verify-email-code
 *
 * Cookies:
 * - Backend sets HttpOnly cookies on /api/verify-email-code response.
 * - React cannot read them, but must use credentials: "include".
 */
export default function TeacherLoginPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const emailTrimmed = useMemo(() => (email || "").trim(), [email]);

  function resetMessages() {
    setStatusMsg("");
    setErrorMsg("");
  }

  function validateEmailBasic(v) {
    // Basic check only; server is the authority.
    const s = (v || "").trim();
    return s.includes("@") && s.includes(".");
  }

  async function postForm(url, bodyObj) {
    const form = new URLSearchParams();
    Object.entries(bodyObj).forEach(([k, v]) => form.append(k, v ?? ""));

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
      credentials: "include", // IMPORTANT: allow Set-Cookie + send cookies later
    });

    const data = await res.json().catch(() => ({}));
    return { res, data };
  }

  async function handleSendCode(e) {
    e.preventDefault();
    resetMessages();

    if (!emailTrimmed) {
      setErrorMsg("Please enter your email address.");
      return;
    }
    if (!validateEmailBasic(emailTrimmed)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    setBusy(true);
    try {
      const { res, data } = await postForm("/api/send-email-code", { email: emailTrimmed });

      if (!res.ok) {
        setErrorMsg(data?.error || "Failed to send code.");
        return;
      }

      setStatusMsg("A login code has been sent to your email.");
      setStep("code");
    } catch (err) {
      setErrorMsg("Network error sending code.");
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    resetMessages();

    const codeTrimmed = (code || "").trim();

    if (!emailTrimmed) {
      setErrorMsg("Missing email address.");
      return;
    }
    if (!/^\d{6}$/.test(codeTrimmed)) {
      setErrorMsg("Please enter a 6-digit code.");
      return;
    }

    setBusy(true);
    try {
      const { res, data } = await postForm("/api/verify-email-code", {
        email: emailTrimmed,
        code: codeTrimmed,
      });

      if (!res.ok) {
        setErrorMsg(data?.error || "Failed to verify code.");
        return;
      }

      // At this point, backend should have returned Set-Cookie headers.
      // Cookies are HttpOnly so we cannot read them in JS.
      // Optional: confirm session and get context, then redirect.
      const ctxRes = await fetch("/api/session-context", { credentials: "include" });
      if (!ctxRes.ok) {
        setErrorMsg("Verified, but session could not be confirmed. Please refresh and try again.");
        return;
      }

      const ctx = await ctxRes.json().catch(() => ({}));
      if (!ctx?.verified) {
        setErrorMsg("Verified, but not logged in. Please try again.");
        return;
      }

      // Redirect to your protected page
      navigate("/teacher-upload-pdf");
    } catch (err) {
      setErrorMsg("Network error verifying code.");
    } finally {
      setBusy(false);
    }
  }

  function handleBackToEmail() {
    resetMessages();
    setCode("");
    setStep("email");
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.iconCircle} aria-hidden="true">
          🎓
        </div>

        <h1 style={styles.title}>Teacher Login</h1>

        {step === "email" ? (
          <>
            <p style={styles.subtitle}>Enter your school email to receive a login code</p>

            <form onSubmit={handleSendCode} style={styles.form}>
              <label style={styles.label}>Email Address</label>
              <div style={styles.inputRow}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teacher@school.edu"
                  style={styles.input}
                  autoComplete="email"
                  disabled={busy}
                />
              </div>

              {errorMsg ? <div style={styles.error}>{errorMsg}</div> : null}
              {statusMsg ? <div style={styles.status}>{statusMsg}</div> : null}

              <button type="submit" style={{ ...styles.button, opacity: busy ? 0.7 : 1 }} disabled={busy}>
                {busy ? "Sending..." : "Send Login Code"}
              </button>
            </form>
          </>
        ) : (
          <>
            <p style={styles.subtitle}>Enter the login code sent to your email</p>

            <form onSubmit={handleVerifyCode} style={styles.form}>
              <label style={styles.label}>Email Address</label>
              <input type="email" value={emailTrimmed} style={{ ...styles.input, background: "#f4f6f8" }} disabled />

              <label style={{ ...styles.label, marginTop: 14 }}>Enter 6-Digit Code</label>
              <input
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                style={styles.input}
                disabled={busy}
                maxLength={6}
              />

              {statusMsg ? <div style={styles.status}>{statusMsg}</div> : <div style={styles.status}>A login code has been sent to your email.</div>}
              {errorMsg ? <div style={styles.error}>{errorMsg}</div> : null}

              <button type="submit" style={{ ...styles.button, opacity: busy ? 0.7 : 1 }} disabled={busy}>
                {busy ? "Verifying..." : "Verify Code"}
              </button>

              <button type="button" onClick={handleBackToEmail} style={styles.linkButton} disabled={busy}>
                Use a different email
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    background: "#ffffff",
    paddingTop: 80,   // 👈 bring it up (adjust 60–120)
    paddingLeft: 20,
    paddingRight: 20,
  },
  card: {
    width: "min(560px, 92vw)",
    border: "1px solid #e6e9ee",
    borderRadius: 12,
    padding: "34px 34px 28px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
  },
  iconCircle: {
    width: 54,
    height: 54,
    borderRadius: 999,
    background: "#eaf2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    fontSize: 22,
  },
  title: {
    textAlign: "center",
    margin: "0 0 6px",
    fontSize: 32,
  },
  subtitle: {
    textAlign: "center",
    margin: "0 0 22px",
    color: "#667085",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontWeight: 600,
    marginBottom: 4,
  },
  inputRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  input: {
    width: "100%",
    border: "1px solid #d0d5dd",
    borderRadius: 8,
    padding: "12px 12px",
    fontSize: 16,
    outline: "none",
  },
  button: {
    marginTop: 10,
    border: "none",
    borderRadius: 10,
    padding: "12px 16px",
    background: "#2f6fed",
    color: "#fff",
    fontSize: 16,
    cursor: "pointer",
  },
  linkButton: {
    marginTop: 10,
    border: "none",
    background: "transparent",
    color: "#2f6fed",
    cursor: "pointer",
    textDecoration: "underline",
    alignSelf: "center",
  },
  error: {
    background: "#fff1f1",
    border: "1px solid #ffd3d3",
    color: "#b42318",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
  },
  status: {
    background: "#eef4ff",
    border: "1px solid #c7d7fe",
    color: "#1d4ed8",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
  },
};
